import React, { useEffect, useState } from 'react'
import Layout from '../components/Layout/Layout'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Line, Pie, Bar } from 'react-chartjs-2'
import { callApi } from '../api/callApi'
import { API_ENDPOINT, API_URL_2 } from '../api/constant'
import { generateColors, toFixedThree } from '../utility/helper'
import DateRangePickerComponent from '../components/UI/Forms/DateRangePickerComponent'
import moment from '../utility/moment-timezone'

ChartJS.register(...registerables)

const ReportManagement: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<any>();
    const today = new Date()
    const [range, setRange] = useState<{
        startDate: Date;
        endDate: Date;
        key: string
    }[]>([
        {
            startDate: new Date(today.getFullYear(), today.getMonth(), 1),
            endDate: today,
            key: 'selection'
        }
    ]);

    useEffect(() => {
        fetchDashboardData()
    }, [])

    const fetchDashboardData = async () => {
        let params = ''
        const selection = range[0];
        if (selection && selection?.startDate && selection?.endDate) {

            const start = moment(selection.startDate).format("YYYY-MM-DD");
            const end = moment(selection.endDate).format("YYYY-MM-DD");
            params += `&startDate=${start}&endDate=${end}`;
        }
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + 'dashboard?' + params, { method: 'GET' })
            setDashboardData(response)
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }

    }

    const handleDownloadReport = async (type: string) => {
        // Build query parameters correctly
        const queryParams: Record<string, string> = {};
        queryParams.status = 'DELIVERED';
        const selection = range[0];
        if (selection?.startDate && selection?.endDate) {
            queryParams.startDate = moment(selection.startDate).format("YYYY-MM-DD");
            queryParams.endDate = moment(selection.endDate).format("YYYY-MM-DD");
        }
        if (type === 'ANDROID' || type === 'IOS' || type === 'web') {
            queryParams.deviceType = type;
        } else if (type === 'sales') {
            queryParams.isDelivered = 'true';
        } else if (type === 'wallet') {
            queryParams.isWallet = 'true'
        } else {
            queryParams.status = 'REFUNDED';
        }

        const params = new URLSearchParams(queryParams).toString();

        try {
            const url = `${API_URL_2}${API_ENDPOINT.order}orders/download-report?${params}`;
            const response = await callApi(url, {
                method: 'POST',
                responseType: 'blob' // Ensure this is handled correctly in callApi
            });

            // Check if the response is valid
            if (!response) {
                throw new Error('Failed to generate report');
            }

            // Determine the correct MIME type (adjust based on actual file type)
            const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // For .xlsx
            // If the file is .xls, use 'application/vnd.ms-excel'

            const blob = new Blob([response], { type: mimeType });
            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Order_Report_${Date.now()}.xlsx`; // Ensure extension matches file type
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading the report:', error);
            // Optionally notify the user of the error
        }
    };


    // Earning Chart Data
    const processEarningData = (dateWiseCount: any) => {
        let sortedDates = Object.keys(dateWiseCount).sort();
        let labels: string[] = [];
        let data: number[] = [];

        if (sortedDates.length === 1) {
            // There is only one date; generate a 7-day span (3 days prior, day of, 3 days after)
            const baseDate = new Date(sortedDates[0]);
            for (let offset = -3; offset <= 3; offset++) {
                const newDate = new Date(baseDate);
                newDate.setDate(newDate.getDate() + offset);
                const formattedDate = newDate.toISOString().split('T')[0];
                labels.push(formattedDate);
                // If the generated date is the existing date, use its sales value; otherwise set it to 0.
                if (formattedDate === sortedDates[0]) {
                    data.push(dateWiseCount[sortedDates[0]].sales);
                } else {
                    data.push(0);
                }
            }
        } else {
            // More than one date exists; use available dates and their sales.
            labels = sortedDates;
            data = sortedDates.map(date => dateWiseCount[date].sales);
        }

        return { labels, data };
    };


    const processOrderCounts = (dateWiseCount: any) => {
        const sortedDates = Object.keys(dateWiseCount).sort();

        if (sortedDates.length === 1) {
            // Only one date exists; generate 7-day range: 3 days before, the date itself, 3 days after
            const baseDate = new Date(sortedDates[0]);
            let labels: string[] = [];
            let data: number[] = [];

            for (let offset = -3; offset <= 3; offset++) {
                const newDate = new Date(baseDate);
                newDate.setDate(newDate.getDate() + offset);
                const formattedDate = newDate.toISOString().split('T')[0];
                labels.push(formattedDate);
                // For the original date, use the actual count; for others, use dummy value 0.
                data.push(formattedDate === sortedDates[0] ? dateWiseCount[sortedDates[0]].count : 0);
            }

            return { labels, data };
        } else {
            // Multiple dates exist; use the available sorted dates and corresponding counts.
            return {
                labels: sortedDates,
                data: sortedDates.map(date => dateWiseCount[date].count)
            };
        }
    };



    const processCategoryData = (categoryWiseCount: any) => {
        const labels = Object.keys(categoryWiseCount);
        const data = labels.map(category => categoryWiseCount[category].sales);
        return { labels, data };
    };

    const topProductsData = (productWiseCount: any) => {
        const labels = Object.keys(productWiseCount);
        const data = labels.map(product => productWiseCount[product].count);
        return { labels, data };
    }

    const refundChartData = (refundChart: any) => {
        const labels = Object.keys(refundChart);
        const data = labels.map(product => refundChart[product].count);
        return { labels, data };
    }

    const { labels: earningLabels, data: earningValues } = processEarningData(dashboardData?.dateWiseCount ? dashboardData?.dateWiseCount : {});
    const { labels: orderCountLabels, data: orderCountValues } = processOrderCounts(dashboardData?.dateWiseCount ? dashboardData?.dateWiseCount : {});
    const { labels: categoryLabels, data: categoryValues } = processCategoryData(dashboardData?.categoryWiseCount ? dashboardData?.categoryWiseCount : {});
    const { labels: topProductsLabels, data: TopProductsValues } = topProductsData(dashboardData?.productWiseCount ? dashboardData?.productWiseCount : {});
    const { labels: refundLabels, data: refundValues } = refundChartData(dashboardData?.refundChart ? dashboardData?.refundChart : {});


    return (
        <Layout>
            <h2 className="app-page-title">Report Overview</h2>

            {/* Stats Cards */}
            <div className="row mb-3">
                <div className='col-5'>

                </div>
                <div className='col-5' >
                    <DateRangePickerComponent
                        initialStartDate={range[0].startDate}
                        initialEndDate={today}
                        onChange={(start, end) => {
                            console.log('Selected Range: data', start, end);
                            setRange([
                                {
                                    startDate: start,
                                    endDate: end,
                                    key: 'selection'
                                }
                            ]);

                        }}
                    />
                </div>
                <div className='col-auto'>
                    <button className="btn btn-primary" onClick={fetchDashboardData}>
                        Apply Filters
                    </button>
                </div>
            </div>
            <div className="row g-4 mb-2 dashboardcards">
                {[
                    {
                        title: "Total Sales",
                        value: "BHD " + toFixedThree(dashboardData?.totalSales || 0),
                        color: "text-success",
                        type: 'sales',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-bar-chart" viewBox="0 0 16 16"><path d="M4 11H2v3h2zm5-4H7v7h2zm5-5v12h-2V2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm-5 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1z"/></svg>`
                    },
                    {
                        title: "Total Refund Amount",
                        value: "BHD " + toFixedThree(dashboardData?.totalRefundAmount || 0),
                        color: "text-danger",
                        type: 'refund',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-box" viewBox="0 0 16 16"><path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z"/></svg>`
                    },
                    {
                        title: "Total Wallet Used",
                        value: "BHD " + toFixedThree(dashboardData?.totalPaidByWallet || 0),
                        color: "text-success",
                        type: 'wallet',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-wallet2" viewBox="0 0 16 16">
  <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5z"/>
</svg>`
                    },
                    {
                        title: 'Android App Orders',
                        value: dashboardData?.orderByDeviceType?.['ANDROID']?.count || 0,
                        color: 'text-success',
                        type: 'ANDROID',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-ANDROID" viewBox="0 0 16 16">
  <path d="M2.76 3.061a.5.5 0 0 1 .679.2l1.283 2.352A8.9 8.9 0 0 1 8 5a8.9 8.9 0 0 1 3.278.613l1.283-2.352a.5.5 0 1 1 .878.478l-1.252 2.295C14.475 7.266 16 9.477 16 12H0c0-2.523 1.525-4.734 3.813-5.966L2.56 3.74a.5.5 0 0 1 .2-.678ZM5 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2m6 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2"/>
</svg>`
                    },
                    {
                        title: 'iOS App Orders',
                        value: dashboardData?.orderByDeviceType?.['IOS']?.count || 0,
                        color: 'text-success',
                        type: 'IOS',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-apple" viewBox="0 0 16 16">
  <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516s1.52.087 2.475-1.258.762-2.391.728-2.43m3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422s1.675-2.789 1.698-2.854-.597-.79-1.254-1.157a3.7 3.7 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56s.625 1.924 1.273 2.796c.576.984 1.34 1.667 1.659 1.899s1.219.386 1.843.067c.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758q.52-1.185.473-1.282"/>
  <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516s1.52.087 2.475-1.258.762-2.391.728-2.43m3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422s1.675-2.789 1.698-2.854-.597-.79-1.254-1.157a3.7 3.7 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56s.625 1.924 1.273 2.796c.576.984 1.34 1.667 1.659 1.899s1.219.386 1.843.067c.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758q.52-1.185.473-1.282"/>
</svg>`
                    },
                    {
                        title: 'Web Orders',
                        value: dashboardData?.orderByDeviceType?.['web']?.count || 0,
                        color: 'text-success',
                        type: 'web',
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-window-desktop" viewBox="0 0 16 16">
  <path d="M3.5 11a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/>
  <path d="M2.375 1A2.366 2.366 0 0 0 0 3.357v9.286A2.366 2.366 0 0 0 2.375 15h11.25A2.366 2.366 0 0 0 16 12.643V3.357A2.366 2.366 0 0 0 13.625 1zM1 3.357C1 2.612 1.611 2 2.375 2h11.25C14.389 2 15 2.612 15 3.357V4H1zM1 5h14v7.643c0 .745-.611 1.357-1.375 1.357H2.375A1.366 1.366 0 0 1 1 12.643z"/>
</svg>`
                    }
                ].map((card, index) => (
                    <div className="col-md-4 cardsnew" key={index}>
                        <div className="app-card innrecard app-card-orders-table shadow-sm mb-4 p-4">
                            <h5 className='text-primary d-flex align-items-center justify-content-between gap-3'>
                                {card.title}
                                <span dangerouslySetInnerHTML={{ __html: card.icon }} />
                            </h5>
                            <div className="d-flex justify-content-between align-items-center">
                                <p className="m-0">{card.value}</p>
                                <button className="btn text-success" onClick={() => handleDownloadReport(card.type)} >Download Report</button>
                            </div>
                        </div>
                    </div>

                ))}
            </div>


            {/* Charts Section */}
            <h2 className="app-page-title mt-2">Sales Analytics</h2>
            <div className="row g-4 mb-4">
                <div className="col-md-8">
                    <div className="app-card app-card-orders-table shadow-sm mb-0 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="card-title mb-0">Earnings Overview</h5>
                        </div>
                        <Line
                            data={{
                                labels: earningLabels,
                                datasets: [{
                                    label: 'Earnings (BHD)',
                                    data: earningValues,
                                    borderColor: 'rgba(75, 192, 192, 1)',
                                    tension: 0.3,
                                }]
                            }}
                            options={{
                                scales: {
                                    x: {
                                        display: false // Hide x-axis labels
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="app-card app-card-orders-table shadow-sm mb-0 p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="card-title mb-0">Sales by Category</h5>
                        </div>
                        <Pie
                            data={{
                                labels: categoryLabels,
                                datasets: [{
                                    data: categoryValues,
                                    backgroundColor: generateColors(categoryLabels.length),
                                }]
                            }}
                        />
                    </div>
                </div>
                <div className="row g-4 mb-4">
                    <div className="col-md-6">
                        <div className="app-card app-card-orders-table shadow-sm mb-0 p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="card-title mb-0">Top Products</h5>
                            </div>
                            <Bar
                                data={{
                                    labels: topProductsLabels,
                                    datasets: [{
                                        label: 'Count',
                                        data: TopProductsValues,
                                        borderColor: 'rgba(75, 192, 192, 1)',
                                        backgroundColor: [
                                            "rgba(255, 99, 132, 0.5)",
                                            "rgba(54, 162, 235, 0.5)",
                                            "rgba(255, 206, 86, 0.5)",
                                            "rgba(75, 192, 192, 0.5)",
                                            "rgba(153, 102, 255, 0.5)",
                                        ],
                                    }]
                                }}
                                options={{
                                    scales: {
                                        x: {
                                            display: false // Hide x-axis labels
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="app-card app-card-orders-table shadow-sm mb-0 p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="card-title mb-0">Refund Analytics</h5>
                            </div>
                            <Bar
                                data={{
                                    labels: refundLabels,
                                    datasets: [{
                                        label: 'Count',
                                        data: refundValues,
                                        borderColor: 'rgba(75, 192, 192, 1)',
                                        backgroundColor: [
                                            "rgba(255, 99, 132, 0.5)",
                                            "rgba(54, 162, 235, 0.5)",
                                            "rgba(255, 206, 86, 0.5)",
                                            "rgba(75, 192, 192, 0.5)",
                                            "rgba(153, 102, 255, 0.5)",
                                        ],
                                    }]
                                }}
                                options={{
                                    scales: {
                                        x: {
                                            display: false // Hide x-axis labels
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="app-card app-card-orders-table shadow-sm mb-0 p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="card-title mb-0">Order Analytics</h5>
                            </div>
                            <Line
                                data={{
                                    labels: orderCountLabels,
                                    datasets: [{
                                        label: 'Order Counts',
                                        data: orderCountValues,
                                        borderColor: 'rgba(75, 192, 192, 1)',
                                        tension: 0.3,
                                    }]
                                }}
                                options={{
                                    scales: {
                                        x: {
                                            display: false // Hide x-axis labels
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </Layout>
    )
}

export default ReportManagement